import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import classNames from 'classnames';

import {default as Modal, withModal} from 'components/Modal';
import PaymentStatusTag from 'components/PaymentStatusTag';

import {Total, BaseInformation, TransactionsTable} from './components';

import {
  SCHEDULE_ITEM_TYPES,
  MODALS,
  FETCH_SCHEDULE_DELAY,
  fetchPayment,
  syncWithXero,
  voidObject,
  addXeroId
} from 'ducks/tenancy';
import {delayedAction, composeActions} from 'ducks/common';
import {
  MODALS as BASE_MODALS,
  openModal,
  closeModal,
  storeDataInModal
} from 'ducks/modals';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import './styles.css';

class PaymentDetailModal extends React.Component<any> {
  componentDidMount() {
    const {data: {paymentId, locationRef}} = this.props;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS, locationRef},
      () => {
        const fetchPaymentExtraActions = this.props.storeDataExtraActions(
          data => ({
            payment: data
          })
        );

        this.props.fetchPayment({paymentId}, fetchPaymentExtraActions);
      }
    );
  }

  openConfirmationModal = ({heading, confirmAction, question}) =>
    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading,
      question,
      confirmAction
    });

  getSuccessExtraActions = () => {
    return [
      ...this.props.closeModalExtraActions,
      ...this.props.refetchExtraActions({
        tenancyId: this.props.data.tenancyId,
        scheduleType: this.props.data.scheduleType
      })
    ];
  };

  delayedFetchScheduleAction = () =>
    delayedAction({
      delay: FETCH_SCHEDULE_DELAY,
      action: () =>
        composeActions(
          this.props.delayExtraActions({
            tenancyId: this.props.data.tenancyId,
            scheduleType: this.props.data.scheduleType
          })
        )
    });

  voidTransactionConfirmAction = () => {
    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      ...this.getSuccessExtraActions(),
      this.delayedFetchScheduleAction
    ];
    const failureExtraAction = [
      ({errors}) =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {errors, submitting: false}
        })
    ];

    this.props.voidObject(
      {
        objectId: this.props.data.payment.transaction.id,
        objectType: SCHEDULE_ITEM_TYPES.PAYMENT,
        scheduleType: this.props.data.scheduleType,
        tenancyId: this.props.data.tenancyId
      },
      successExtraActions,
      failureExtraAction
    );
  };

  voidTransaction = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.VOID_SCHEDULE_ITEMS
      },
      () =>
        this.openConfirmationModal({
          heading: 'Void Payment',
          question: 'Are you sure you want to void this payment?',
          confirmAction: this.voidTransactionConfirmAction
        })
    );

  syncWithXeroConfirmAction = () => {
    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      ...this.getSuccessExtraActions(),
      this.delayedFetchScheduleAction
    ];
    const failureExtraAction = [
      ({errors}) =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {errors, submitting: false}
        })
    ];

    this.props.syncWithXero(
      {
        objectId: this.props.data.paymentId,
        objectType: SCHEDULE_ITEM_TYPES.PAYMENT,
        scheduleType: this.props.data.scheduleType,
        tenancyId: this.props.data.tenancyId
      },
      successExtraActions,
      failureExtraAction
    );
  };

  syncPayment = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.SYNC_WITH_XERO
      },
      () =>
        this.openConfirmationModal({
          heading: 'Sync Payment',
          question: 'Are you sure you want to sync this payment with xero?',
          confirmAction: this.syncWithXeroConfirmAction
        })
    );

  addXeroIdToPayment = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.ADD_XERO_ID
      },
      () => {
        const xeroId = window.prompt('Xero ID: ', '');
        if (!_.isEmpty(xeroId)) {
          const successExtraActions = [
            ...this.getSuccessExtraActions(),
            this.delayedFetchScheduleAction
          ];
          const failureExtraActions = this.props.storeDataExtraActions(
            ({errors}) => ({errors})
          );
          this.props.addXeroId(
            {
              xeroId,
              objectId: this.props.data.paymentId,
              objectType: SCHEDULE_ITEM_TYPES.PAYMENT,
              scheduleType: this.props.data.scheduleType,
              tenancyId: this.props.data.tenancyId
            },
            successExtraActions,
            failureExtraActions
          );
        }
      }
    );

  openInvoiceDetailModal = invoice =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS
      },
      () => {
        const {scheduleType, locationRef, tenancyId} = this.props.data;

        this.props.composeActions([
          () => closeModal(MODALS.PAYMENT_DETAIL),

          () =>
            openModal(
              MODALS.INVOICE_DETAIL,
              {
                scheduleType,
                invoiceId: invoice.id,
                locationRef,
                tenancyId
              },
              true
            )
        ]);
      }
    );

  render() {
    const payment = _.get(this.props, 'data.payment');
    const errors = _.get(this.props, 'data.errors', {});
    const xeroSyncErrors = _.get(this.props, 'data.payment.xeroErrors', []);

    return (
      <div>
        {payment && (
          <Modal
            show={true}
            onClose={this.props.closeModal}
            className={classNames('PaymentDetailModal', {
              statusPadded: payment && payment.voided
            })}>
            <Modal.Header>
              <h3>Transaction: Payment</h3>
              <PaymentStatusTag status={payment.status} />
            </Modal.Header>
            <Modal.Body>
              <Modal.Errors errors={errors} />
              <Modal.Errors errors={xeroSyncErrors} />

              <BaseInformation
                payment={payment}
                voidTransaction={this.voidTransaction}
                syncPayment={this.syncPayment}
                addXeroIdToPayment={this.addXeroIdToPayment}
              />
              <TransactionsTable
                onInvoiceClick={this.openInvoiceDetailModal}
                payment={payment}
              />
              <Total total={payment.amount} />
            </Modal.Body>
          </Modal>
        )}
      </div>
    );
  }
}

const mapDispatchToProps = {
  fetchPayment,
  syncWithXero,
  voidObject,
  addXeroId,
  requiresPermission,
  delayedAction,
  openModal,
  composeActions
};

export default connect(null, mapDispatchToProps)(withModal(
  MODALS.PAYMENT_DETAIL
)(PaymentDetailModal) as React.ComponentType<any>);
